import { cn } from '@shared/lib';
import { Input, type InputProps } from '../Input';
import { Search, X } from '@ui/icons';

export interface SearchInputProps extends Omit<
  InputProps,
  'startIcon' | 'endIcon' | 'type' | 'tone'
> {
  /** Shows a clear button when the (controlled) value is non-empty; called when pressed. */
  onClear?: (() => void) | undefined;
}

/**
 * Search field — a pill `Input` with a trailing search icon (Figma header search). Shows a clear
 * button instead when there is a value + `onClear`. **A11y:** `type="search"` (searchbox role);
 * label via `aria-label` or a `Field`.
 */
export function SearchInput({
  onClear,
  value,
  className,
  placeholder,
  ...props
}: SearchInputProps) {
  const showClear = onClear !== undefined && typeof value === 'string' && value.length > 0;

  return (
    <Input
      type="search"
      value={value}
      placeholder={placeholder}
      className={cn(
        'h-11 rounded-full text-sm placeholder:text-muted-soft [&::-webkit-search-cancel-button]:hidden',
        className,
      )}
      endIcon={
        showClear ? (
          <button
            type="button"
            aria-label="Clear search"
            onClick={() => onClear()}
            className="flex items-center rounded-sm p-1 text-muted-soft hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="size-4" />
          </button>
        ) : (
          <Search className="size-4" />
        )
      }
      {...props}
    />
  );
}
